import { formatLocaleCurrency, getCurrency } from "country-currency-map";
import TokenManager from "utils/TokenManager";

export const nonStandardCurrencyString = {
  "European Euro": "EUR",
  "Swiss Franc": "CHF",
  "British pound": "GBP",
  "Canadian dollar": "CAD",
  "Chinese Yuan (RMB)": "CNY",
  USA: "USD",
  YUAN: "CNY",
  AFN: "AFA",
  DOLLAR: "USD",
}; // update all non standard format here with correct format value

// Currencies that share the "$" symbol with USD get a disambiguated prefix
// following the Unicode CLDR / Intl.NumberFormat standard used by most financial systems.
export const disambiguatingSymbols = {
  MXN: "MX$",
  CAD: "CA$",
  AUD: "A$",
  HKD: "HK$",
  SGD: "S$",
  NZD: "NZ$",
  TWD: "NT$",
  BND: "B$",
};

const getDisambiguatedSymbol = (currencyCode, rawSymbol) =>
  disambiguatingSymbols[currencyCode] ?? rawSymbol;

export const currencySymbol = () => {
  const code = TokenManager.getCurrencyCode();
  const raw = getCurrency(code).symbolFormat.replace("{#}", "");
  return getDisambiguatedSymbol(code, raw);
};

const formatCurrency = (amount, currency, shortCode = false) => {
  // Callers throughout utils/index.js declare `currency = TokenManager.getCurrencyCode()`
  // as a default param, but JS defaults only trigger on `undefined` -- an
  // explicit `null`/`""` (e.g. a resolved-but-absent currency from the
  // backend) sails through as-is. Falling back here, once, protects every
  // caller instead of requiring each one to remember `|| undefined`.
  currency = currency || TokenManager.getCurrencyCode();
  let convertedAmount =
    !amount ||
    amount === "undefined" ||
    amount === "0" ||
    amount === null ||
    typeof amount === "string"
      ? 0
      : amount;
  const updatedCurrency = nonStandardCurrencyString[currency]
    ? nonStandardCurrencyString[currency]
    : currency;
  const _currencySymbol = getCurrency(updatedCurrency)
    ? getDisambiguatedSymbol(
        updatedCurrency,
        getCurrency(updatedCurrency).symbolFormat.replace("{#}", "")
      )
    : updatedCurrency;
  const localCurrency = formatLocaleCurrency(0, updatedCurrency, {
    locale: "en-US",
  }).replace("0", "");
  const isnegative = Number(convertedAmount) < 0;
  if (shortCode) {
    let shortAbbrUnit = "";
    const absNumb = Math.abs(convertedAmount);
    const sign = Math.sign(convertedAmount);
    if (absNumb > 999999999) {
      convertedAmount = sign * (absNumb / 1000000000);
      shortAbbrUnit = "B";
    } else if (absNumb > 999999) {
      convertedAmount = sign * (absNumb / 1000000);
      shortAbbrUnit = "M";
    } else if (absNumb > 99999) {
      convertedAmount = sign * (absNumb / 1000);
      shortAbbrUnit = "K";
    }
    let str = `${formatLocaleCurrency(convertedAmount, updatedCurrency, {
      abbreviate: shortCode,
      locale: "en-US",
    })}${shortAbbrUnit}`;
    if (str && localCurrency.length >= 3 && localCurrency !== _currencySymbol) {
      str = str.replace(localCurrency, _currencySymbol).trim();
    }
    return `${isnegative ? "-" : ""}${str.substring(str.indexOf(_currencySymbol))}`
      .replace("--", "-")
      .replace("NaN", "0");
  }

  let str = `${formatLocaleCurrency(amount, updatedCurrency, {
    abbreviate: shortCode,
    locale: "en-US",
  })}`;
  if (str && localCurrency.length >= 3 && localCurrency !== _currencySymbol) {
    str = str.replace(localCurrency, _currencySymbol).trim();
  }
  return `${isnegative ? "-" : ""}${str.substring(
    str.indexOf(_currencySymbol)
  )}`
    .replace("--", "-")
    .replace("NaN", "0");
};

export default formatCurrency;

export const checkNonstandardCurrencyFormat = (
  currency,
  handleError = false
) => {
  if (!currency) return;
  const baseClientCurrency = TokenManager.getCurrencyCode();
  if (getCurrency(currency)) {
    if (handleError) {
      return false;
    }
    return currency;
  } else {
    if (handleError) {
      return nonStandardCurrencyString[currency] ? false : true;
    }
    return nonStandardCurrencyString[currency] || baseClientCurrency;
  }
};
